import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, Alert, CircularProgress, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { serverServicesAPI } from '../services/api';
import { saveBlob, downloadError } from '../utils/download';
import ServiceControl from './ServiceControl';
import RecentServiceActions from './RecentServiceActions';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const when = (d) => (d ? new Date(d).toLocaleString() : '-');
const ago = (d) => {
  if (!d) return '-';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  return hours < 48 ? `${hours} h ${mins % 60} min` : `${Math.floor(hours / 24)} days ${hours % 24} h`;
};
const cpu = (s) => (s == null ? '-' : s < 120 ? `${s} s` : `${Math.round(s / 60)} min`);

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);
const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };

// The job's script (.srv) and its daily logs, which an engineer can download. The server sends the file to the portal first, so it can take a few seconds.
function JobFiles({ customerCode, host, pid }) {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [log, setLog] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let current = true;
    serverServicesAPI.jobFiles(customerCode, host, pid)
      .then((res) => { if (current) { setFiles(res.data); setLog(res.data.logs[0]?.name || ''); } })
      .catch((err) => { if (current) setError(err.response?.data?.detail || 'Could not list the files'); });
    return () => { current = false; };
  }, [customerCode, host, pid]);

  const fetchFile = async (kind, name) => {
    setBusy(kind); setError(null);
    try {
      const res = await serverServicesAPI.downloadJobFile(customerCode, host, pid, kind, name);
      saveBlob(res.data, `${host}_${kind === 'log' ? name : files.script.name}`);
    } catch (err) { setError(await downloadError(err, 'Could not download the file')); }
    setBusy(null);
  };

  if (error && !files) return <Alert severity="warning" sx={{ my: 1 }}>{error}</Alert>;
  if (!files) return <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>;
  const chosen = files.logs.find((l) => l.name === log);
  const tooBig = (size) => size > files.max_bytes;
  return (
    <Box>
      <Typography variant="subtitle2">Files</Typography>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', my: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 70 }} color="text.secondary">Script</Typography>
        {files.script ? (
          <>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{files.script.name} ({fmtSize(files.script.size)})</Typography>
            <Button size="small" variant="outlined" startIcon={busy === 'script' ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={!!busy || tooBig(files.script.size)} onClick={() => fetchFile('script')}>Download script</Button>
          </>
        ) : <Typography variant="body2">No script file was found for this job.</Typography>}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', my: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 70 }} color="text.secondary">Log</Typography>
        {files.logs.length > 0 ? (
          <>
            <TextField select size="small" label="Day" value={log} onChange={(e) => setLog(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 240 }}>
              {files.logs.map((l) => <option key={l.name} value={l.name}>{l.name.replace('.log', '')} ({fmtSize(l.size)})</option>)}
            </TextField>
            <Button size="small" variant="outlined" startIcon={busy === 'log' ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={!!busy || !chosen || tooBig(chosen.size)} onClick={() => fetchFile('log', log)}>Download log</Button>
            {chosen && tooBig(chosen.size) && <Typography variant="caption" color="error">Too large to download here (limit {fmtSize(files.max_bytes)}). Ask Cloud Services.</Typography>}
          </>
        ) : <Typography variant="body2">{files.log_folder_found ? 'No daily log files were found.' : 'The job\'s log folder was not found.'}</Typography>}
      </Box>
      <Typography variant="caption" color="text.secondary">The newest 60 daily logs are listed. Downloads are recorded with your name.</Typography>
    </Box>
  );
}

const fileName = (path) => (path ? String(path).split(/[\\/]/).pop() : '-');

// One job's configuration: the config file, STARLIMS user and script it was started with, its databases and its settings (secrets are masked on the server)
function JobConfig({ customerCode, host, pid }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let current = true;
    serverServicesAPI.jobConfig(customerCode, host, pid)
      .then((res) => { if (current) setData(res.data); })
      .catch((err) => { if (current) setError(errText(err, 'Could not read the configuration')); });
    return () => { current = false; };
  }, [customerCode, host, pid]);

  if (error) return <Alert severity="warning" sx={{ my: 1 }}>{error}</Alert>;
  if (!data) return <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>;
  return (
    <Box sx={{ py: 1.5, display: 'grid', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Config file</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} title={data.config_file}>{fileName(data.config_file)}</Typography>
        <Typography variant="body2" color="text.secondary">STARLIMS user</Typography>
        <Typography variant="body2">{data.user || '-'}</Typography>
        <Typography variant="body2" color="text.secondary">Script</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} title={data.script_file}>{data.script_file || '-'}</Typography>
      </Box>
      {data.connections.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Databases</Typography>
          <Table size="small">
            <TableBody>
              {data.connections.map((c) => (
                <TableRow key={c.name}>
                  <TableCell sx={{ width: '25%' }}>{c.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.server}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.database}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {data.settings.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Settings</Typography>
          <Table size="small">
            <TableBody>
              {data.settings.map((x) => (
                <TableRow key={x.key}>
                  <TableCell sx={{ width: '35%', verticalAlign: 'top' }}>{x.key}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{x.value === '***' ? <Chip size="small" variant="outlined" label="hidden" /> : (x.value || '-')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <JobFiles customerCode={customerCode} host={host} pid={pid} />
      <Typography variant="caption" color="text.secondary">Passwords and other secret settings are never shown. Read at {when(data.checked_at)}.</Typography>
    </Box>
  );
}

function JobRow({ job, customerCode, host }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={`Show details of ${job.job}`} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>{job.job}</TableCell>
        <TableCell>{when(job.started)}</TableCell>
        <TableCell>{ago(job.started)}</TableCell>
        <TableCell align="right">{job.memory_mb} MB</TableCell>
        <TableCell align="right">{cpu(job.cpu_seconds)}</TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{job.pid}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit><JobConfig customerCode={customerCode} host={host} pid={job.pid} /></Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// The job programs a STARLIMS Manager Service has started on one server (Starlims.Server.WorkerProcess.<Job>.exe), read live
function ServiceJobsDialog({ customerCode, host, service, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await serverServicesAPI.jobs(customerCode, host)).data); }
    catch (err) { setData(null); setError(errText(err, 'Could not read the jobs')); }
    finally { setLoading(false); }
  }, [customerCode, host]);

  useEffect(() => { load(); }, [load]);

  const workers = (data?.jobs || []).filter((j) => j.is_worker);
  const others = (data?.jobs || []).filter((j) => !j.is_worker);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="jobs-title">
      <DialogTitle id="jobs-title" sx={{ pr: 6 }}>
        Jobs running on {host}
        <Typography variant="body2" color="text.secondary">{data?.service || service}</Typography>
        <IconButton aria-label="Close jobs" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
              <Chip size="small" color={data.state === 'Running' ? 'success' : 'warning'} label={data.state || 'Not installed'} />
              {data.started && <Chip size="small" variant="outlined" label={`Service up since ${when(data.started)} (${ago(data.started)})`} />}
              {data.memory_mb != null && <Chip size="small" variant="outlined" label={`Service memory ${data.memory_mb} MB`} />}
              <Chip size="small" variant="outlined" label={`${workers.length} job${workers.length === 1 ? '' : 's'} running`} />
              <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
              {data.found && <ServiceControl customerCode={customerCode} host={host} serviceKey="manager" serviceLabel={data.service || service} state={data.state} jobCount={workers.length} onChanged={load} />}
            </Box>
            {workers.length === 0 && <Alert severity="info">The service has not started any jobs at the moment.</Alert>}
            {workers.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Job</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Running for</TableCell>
                      <TableCell align="right">Memory</TableCell>
                      <TableCell align="right">CPU time</TableCell>
                      <TableCell align="right">Process</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workers.map((j) => <JobRow key={j.pid} job={j} customerCode={customerCode} host={host} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {others.length > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Other programs started by the service: {others.map((o) => o.program).join(', ')}.</Typography>}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Each job is a separate worker program the service starts; open a row for its configuration. A job that finishes disappears from this list; read at {when(data.checked_at)}.
            </Typography>
          </>
        )}
        <RecentServiceActions customerCode={customerCode} host={host} refresh={data?.checked_at} />
      </DialogContent>
    </Dialog>
  );
}

export default ServiceJobsDialog;
